import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import Translations from 'src/core/utils/Translations';
import { useMessagesStore } from 'src/domain/stores/useMessagesStore';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';

const initialState = {
	isLogoutDialogVisible: false,
	isSendMessageDialogVisible: false,
	isMessageSentDialogVisible: false,
	message: null as string | null,
	subject: null as string | null,
	reservationNumber: null as string | null,
	selectedWorkspaceId: null as string | null,
	messageError: null as string | null,
	isSendingMessage: false,
};

export const useAccountStore = create(
	combine({ ...initialState }, (set, get) => {
		const reset = () => set({ ...initialState });

		const init = () => reset();

		const postMessage = async ({
			onSuccess,
			onError,
			onFinally,
		}: {
			onSuccess?: () => void;
			onError?: (error: ApiRequestError) => void;
			onFinally?: () => void;
		} = {}) => {
			const { selectedWorkspaceId, subject, message, reservationNumber } = get();

			if (selectedWorkspaceId === null) {
				set({ messageError: Translations.selectResortError });
				return;
			}

			if (subject === null) {
				set({ messageError: Translations.enterSubject });
				return;
			}

			if (message === null) {
				set({ messageError: Translations.enterMessage });
				return;
			}

			set({ messageError: null, isSendingMessage: true });

			await useMessagesStore.getState().postApiMessage({
				message,
				subject,
				workspaceId: selectedWorkspaceId,
				reservationNumber,
				onSuccess: () => {
					set({ ...initialState, isMessageSentDialogVisible: true });
					onSuccess?.();
				},
				onError: (error) => onError?.(error),
				onFinally: () => {
					set({ isSendingMessage: false });
					onFinally?.();
				},
			});
		};

		const setLogoutDialogVisible = (isLogoutDialogVisible: boolean) => set({ isLogoutDialogVisible });

		const openSendMessageDialog = () => {
			useMessagesStore.getState().clearSendMessageErrors();
			set({ isSendMessageDialogVisible: true, messageError: null });
		};

		const closeSendMessageDialog = () => set({ isSendMessageDialogVisible: false });

		const setMessageSentDialogVisible = (isMessageSentDialogVisible: boolean) => set({ isMessageSentDialogVisible });

		const setMessage = (message: string) => set({ message });

		const setSubject = (subject: string) => set({ subject });

		const setReservationNumber = (reservationNumber: string) => set({ reservationNumber });

		const setSelectedWorkspaceId = (selectedWorkspaceId: string) => set({ selectedWorkspaceId });

		return {
			init,
			postMessage,
			setLogoutDialogVisible,
			openSendMessageDialog,
			closeSendMessageDialog,
			setMessageSentDialogVisible,
			setMessage,
			setSubject,
			setReservationNumber,
			setSelectedWorkspaceId,
			reset,
		};
	}),
);
